import React, { useState, useEffect } from 'react';
import { Spin } from 'antd';
import {
    MessageOutlined,
    StarOutlined,
    QuestionCircleOutlined,
    BookOutlined,
    TeamOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { adminService } from '../../services/adminService';

const AdminDashboardPage = () => {
    const navigate = useNavigate();
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchStats = async () => {
            try {
                const data = await adminService.getDashboardStats();
                setStats(data);
            } catch (err) {
                console.error('Dashboard stats fetch error:', err);
            } finally {
                setLoading(false);
            }
        };
        fetchStats();
    }, []);

    const statCards = stats ? [
        {
            title: 'Bugünkü Sohbetler',
            value: stats.todaySessions,
            icon: <MessageOutlined />,
            color: 'primary',
        },
        {
            title: 'Ortalama Puan',
            value: stats.averageRating ?? 0,
            icon: <StarOutlined />,
            color: 'warning',
            subtitle: `${stats.totalFeedback} değerlendirme`,
            isDecimal: true,
        },
        {
            title: 'Cevaplanamayan Sorular',
            value: stats.pendingQuestions,
            icon: <QuestionCircleOutlined />,
            color: 'danger',
            path: '/admin/unanswered-questions',
        },
        {
            title: 'Bilgi Bankası',
            value: stats.knowledgeBaseEntries,
            icon: <BookOutlined />,
            color: 'secondary',
            path: '/admin/knowledge-base',
        },
        {
            title: 'Temsilciler',
            value: stats.totalAgents,
            icon: <TeamOutlined />,
            color: 'success',
            path: '/admin/agents',
        },
    ] : [];

    const containerVariants = {
        hidden: { opacity: 0 },
        visible: { opacity: 1, transition: { staggerChildren: 0.08 } },
    };
    const itemVariants = {
        hidden: { opacity: 0, y: 16 },
        visible: { opacity: 1, y: 0 },
    };

    if (loading) {
        return <div className="admin-loading"><Spin size="large" /></div>;
    }

    return (
        <div>
            <div className="admin-page-header">
                <div>
                    <h1 className="admin-page-title">Genel Bakış</h1>
                    <p className="admin-page-subtitle">
                        Sistem durumunu ve istatistikleri buradan takip edebilirsiniz.
                    </p>
                </div>
            </div>

            {/* Stat Cards */}
            <motion.div
                className="admin-stats-grid"
                variants={containerVariants}
                initial="hidden"
                animate="visible"
            >
                {statCards.map((stat, index) => (
                    <motion.div
                        key={index}
                        className="admin-stat-card"
                        variants={itemVariants}
                        whileHover={{ y: -2, transition: { duration: 0.15 } }}
                        onClick={() => stat.path && navigate(stat.path)}
                        style={stat.path ? { cursor: 'pointer' } : undefined}
                    >
                        <div className="admin-stat-card-header">
                            <div className={`admin-stat-card-icon ${stat.color}`}>
                                {stat.icon}
                            </div>
                            {stat.subtitle && (
                                <div className="admin-stat-card-trend">
                                    {stat.subtitle}
                                </div>
                            )}
                        </div>
                        <div className="admin-stat-card-value">
                            {stat.isDecimal ? (
                                <CountUp end={stat.value} duration={1} decimals={1} />
                            ) : (
                                <CountUp end={stat.value} duration={1} />
                            )}
                        </div>
                        <div className="admin-stat-card-label">{stat.title}</div>
                    </motion.div>
                ))}
            </motion.div>
        </div>
    );
};

const CountUp = ({ end, duration = 1, decimals = 0 }) => {
    const [count, setCount] = useState(0);

    useEffect(() => {
        let startTime;
        let animationFrame;
        const factor = Math.pow(10, decimals);

        const animate = (timestamp) => {
            if (!startTime) startTime = timestamp;
            const progress = Math.min((timestamp - startTime) / (duration * 1000), 1);
            setCount(Math.floor(progress * end * factor) / factor);
            if (progress < 1) {
                animationFrame = requestAnimationFrame(animate);
            }
        };

        animationFrame = requestAnimationFrame(animate);
        return () => { if (animationFrame) cancelAnimationFrame(animationFrame); };
    }, [end, duration, decimals]);

    return <>{decimals > 0 ? count.toFixed(decimals) : count}</>;
};

export default AdminDashboardPage;
